// Centralised error codes & user-friendly messages for ALL apps.
// Keep this file small (pure enum + const) with no external deps.
// --------------------------------------------------------------

export enum ErrorCode {
  // Authentication
  INVALID_OTP = 'INVALID_OTP',
  EXPIRED_OTP = 'EXPIRED_OTP',
  OTP_REQUIRED = 'OTP_REQUIRED',
  PHONE_ALREADY_REGISTERED = 'PHONE_ALREADY_REGISTERED',
  EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  VERIFICATION_REQUIRED = 'VERIFICATION_REQUIRED',

  // Validation
  INVALID_PHONE = 'INVALID_PHONE',
  INVALID_EMAIL = 'INVALID_EMAIL',
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  INVALID_FORMAT = 'INVALID_FORMAT',
  VALIDATION_ERROR = 'VALIDATION_ERROR',

  // Rate limiting
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  OTP_RESEND_LIMIT = 'OTP_RESEND_LIMIT',

  // System
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  REQUEST_TIMEOUT = 'REQUEST_TIMEOUT',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

  // Fabric / Profile specific
  CONTACT_INFO_REQUIRED = 'CONTACT_INFO_REQUIRED',
  BUYER_ID_OR_CONTACT_REQUIRED = 'BUYER_ID_OR_CONTACT_REQUIRED',
  INVALID_VERIFICATION_STATUS = 'INVALID_VERIFICATION_STATUS',
  FABRIC_REQUEST_CREATION_FAILED = 'FABRIC_REQUEST_CREATION_FAILED',
  FABRIC_REQUEST_VALIDATION_FAILED = 'FABRIC_REQUEST_VALIDATION_FAILED',
  INVALID_PROFILE_DATA_STRUCTURE = 'INVALID_PROFILE_DATA_STRUCTURE',
  ROLE_MISMATCH = 'ROLE_MISMATCH',
  INVALID_USER_TYPE = 'INVALID_USER_TYPE',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  PROFILE_VALIDATION_FAILED = 'PROFILE_VALIDATION_FAILED',
  LOCATION_REQUIRED = 'LOCATION_REQUIRED',
  PREFERRED_FABRICS_REQUIRED = 'PREFERRED_FABRICS_REQUIRED',
  GENDER_REQUIRED = 'GENDER_REQUIRED',
  DATE_OF_BIRTH_REQUIRED = 'DATE_OF_BIRTH_REQUIRED',
  COMPANY_NAME_REQUIRED = 'COMPANY_NAME_REQUIRED',
  COMPANY_TYPE_REQUIRED = 'COMPANY_TYPE_REQUIRED',
  REGISTRATION_NUMBER_REQUIRED = 'REGISTRATION_NUMBER_REQUIRED',
  FABRICS_SOLD_REQUIRED = 'FABRICS_SOLD_REQUIRED',

  // Generic
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  OPERATION_FAILED = 'OPERATION_FAILED',
}

// User-friendly messages.  ONE place to edit copy.
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  // Authentication
  [ErrorCode.INVALID_OTP]: 'The verification code you entered is incorrect. Please check and try again.',
  [ErrorCode.EXPIRED_OTP]: 'The verification code has expired. Please request a new one.',
  [ErrorCode.OTP_REQUIRED]: 'Please enter the verification code.',
  [ErrorCode.PHONE_ALREADY_REGISTERED]: 'This phone number is already registered. Try logging in instead.',
  [ErrorCode.EMAIL_ALREADY_EXISTS]: 'This e-mail is already registered. Try logging in instead.',
  [ErrorCode.INVALID_CREDENTIALS]: 'Invalid credentials provided. Please try again.',
  [ErrorCode.VERIFICATION_REQUIRED]: 'Please verify your contact details to continue.',

  // Validation
  [ErrorCode.INVALID_PHONE]: 'Please enter a valid phone number.',
  [ErrorCode.INVALID_EMAIL]: 'Please enter a valid e-mail address.',
  [ErrorCode.REQUIRED_FIELD]: 'This field is required.',
  [ErrorCode.INVALID_FORMAT]: 'Please check the format of your input.',
  [ErrorCode.VALIDATION_ERROR]: 'Some fields are invalid. Please review and try again.',

  // Rate limiting
  [ErrorCode.TOO_MANY_REQUESTS]: 'Too many attempts. Please wait a moment before trying again.',
  [ErrorCode.OTP_RESEND_LIMIT]: 'Too many OTP requests. Please wait before requesting another code.',

  // System
  [ErrorCode.NETWORK_ERROR]: 'Unable to connect. Please check your internet connection.',
  [ErrorCode.SERVER_ERROR]: 'Something went wrong on our end. Please try again later.',
  [ErrorCode.REQUEST_TIMEOUT]: 'The request took too long. Please try again.',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'Service is temporarily unavailable. Please try again later.',

  // Fabric / Profile specific
  [ErrorCode.CONTACT_INFO_REQUIRED]: 'Please provide your contact information to proceed.',
  [ErrorCode.BUYER_ID_OR_CONTACT_REQUIRED]: 'A buyer ID or contact information is required.',
  [ErrorCode.INVALID_VERIFICATION_STATUS]: 'Verification status is invalid for this operation.',
  [ErrorCode.FABRIC_REQUEST_CREATION_FAILED]: 'Unable to create fabric request. Please try again.',
  [ErrorCode.FABRIC_REQUEST_VALIDATION_FAILED]: 'Fabric request validation failed. Please review and try again.',
  [ErrorCode.INVALID_PROFILE_DATA_STRUCTURE]: 'Profile data structure is invalid. Please review and try again.',
  [ErrorCode.ROLE_MISMATCH]: 'The specified role does not match the expected value.',
  [ErrorCode.INVALID_USER_TYPE]: 'Invalid user type provided.',
  [ErrorCode.MISSING_REQUIRED_FIELD]: 'A required field is missing.',
  [ErrorCode.PROFILE_VALIDATION_FAILED]: 'Profile validation failed. Please review and try again.',
  [ErrorCode.LOCATION_REQUIRED]: 'Location information is required.',
  [ErrorCode.PREFERRED_FABRICS_REQUIRED]: 'Please specify preferred fabrics.',
  [ErrorCode.GENDER_REQUIRED]: 'Gender is required.',
  [ErrorCode.DATE_OF_BIRTH_REQUIRED]: 'Date of birth is required.',
  [ErrorCode.COMPANY_NAME_REQUIRED]: 'Company name is required.',
  [ErrorCode.COMPANY_TYPE_REQUIRED]: 'Company type is required.',
  [ErrorCode.REGISTRATION_NUMBER_REQUIRED]: 'Registration number is required.',
  [ErrorCode.FABRICS_SOLD_REQUIRED]: 'Please specify fabrics sold.',

  // Generic
  [ErrorCode.UNKNOWN_ERROR]: 'Something went wrong. Please try again.',
  [ErrorCode.OPERATION_FAILED]: 'Operation failed. Please try again.',
};

// Convenience: map HTTP status to default codes (for non-JSON responses)
export const HTTP_STATUS_TO_ERROR: Record<number, ErrorCode> = {
  400: ErrorCode.INVALID_FORMAT,
  401: ErrorCode.INVALID_CREDENTIALS,
  403: ErrorCode.OPERATION_FAILED,
  404: ErrorCode.NETWORK_ERROR,
  408: ErrorCode.REQUEST_TIMEOUT,
  409: ErrorCode.VALIDATION_ERROR,
  429: ErrorCode.TOO_MANY_REQUESTS,
  500: ErrorCode.SERVER_ERROR,
  502: ErrorCode.SERVER_ERROR,
  503: ErrorCode.SERVER_ERROR,
  504: ErrorCode.REQUEST_TIMEOUT,
};

// ---- Shared response shapes ----
export interface ApiErrorResponse {
  success: false;
  errorCode: ErrorCode;
  message: string;
  timestamp: string;
  statusCode: number;
  details?: any;
}

export interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  timestamp: string;
  statusCode: number;
  message?: string;
}

export const createErrorResponse = (
  errorCode: ErrorCode,
  message: string,
  statusCode: number,
  details?: any
): ApiErrorResponse => ({
  success: false,
  errorCode,
  message,
  timestamp: new Date().toISOString(),
  statusCode,
  ...(details && { details }),
});

export const createSuccessResponse = <T>(
  data: T,
  statusCode = 200,
  message?: string
): ApiSuccessResponse<T> => ({
  success: true,
  data,
  timestamp: new Date().toISOString(),
  statusCode,
  ...(message && { message }),
});

// Original helper retained for backward-compatibility
export const mapHttpStatusToErrorCode = (status: number, message?: string): ErrorCode => {
  const defaultCode = HTTP_STATUS_TO_ERROR[status] ?? ErrorCode.UNKNOWN_ERROR;
  if (!message) return defaultCode;

  const lower = message.toLowerCase();
  if (status === 400) {
    if (lower.includes('otp') || lower.includes('verification')) return ErrorCode.INVALID_OTP;
    if (lower.includes('phone')) return ErrorCode.INVALID_PHONE;
    if (lower.includes('email')) return ErrorCode.INVALID_EMAIL;
    return ErrorCode.INVALID_FORMAT;
  }
  if (status === 401) {
    if (lower.includes('expired')) return ErrorCode.EXPIRED_OTP;
    if (lower.includes('otp') || lower.includes('verification')) return ErrorCode.INVALID_OTP;
    return ErrorCode.INVALID_CREDENTIALS;
  }
  if (status === 409) {
    if (lower.includes('phone') || lower.includes('registered')) return ErrorCode.PHONE_ALREADY_REGISTERED;
    return ErrorCode.OPERATION_FAILED;
  }
  return defaultCode;
};
